import { z } from 'zod';

import { isValidISODate } from '../date';
import { CURRENCY_CODES } from '../money';

/** Идентификаторы у нас — cuid из Prisma. */
export const zId = z.string().min(1).max(64);

export const zISODate = z
  .string()
  .refine(isValidISODate, { message: 'validation.invalidDate' });

export const zOptionalISODate = zISODate.nullish();

export const zCurrency = z
  .string()
  .transform((value) => value.toUpperCase())
  .refine((value) => CURRENCY_CODES.includes(value), { message: 'validation.unknownCurrency' });

/* ─── Деньги ────────────────────────────────────────────────────────────── */

/**
 * Потолок денежной величины в минорных единицах.
 *
 * Почему не `Number.MAX_SAFE_INTEGER`. В базе деньги лежат `BigInt` и точны,
 * а приложение читает их через `Number` — за пределами 2⁵³ это преобразование
 * перестаёт быть точным. Но одного лишь «влезает в безопасное целое» мало:
 * остаток кошелька считается суммой операций, и два значения по 9·10¹⁵ дают
 * переполнение уже вдвоём. Проверка каждого поля по отдельности такую сумму
 * не поймает.
 *
 * Поэтому потолок с запасом на сложение: 10¹³ минорных единиц — это сто
 * миллиардов в основных единицах валюты. Больше любой мыслимой суммы для
 * организации на десять человек, и при этом нужно почти девятьсот таких
 * записей подряд, чтобы их сумма вышла за безопасное целое.
 *
 * Верхняя граница здесь — не бизнес-правило, а защита инварианта «деньги
 * всегда целые минорные единицы». Опечатка в лишние пять цифр отвергается
 * заодно, но ради этого правило не вводилось.
 */
export const MAX_MONEY_MINOR = 10_000_000_000_000;

/**
 * Общая основа денежного поля.
 *
 * `.finite()` не декоративен: JSON не умеет записывать `Infinity` буквально,
 * зато `JSON.parse('1e999')` возвращает именно его, и такое число приходит
 * по сети обычным запросом. `.int()` отсекает дробные и заодно `NaN`.
 */
const money = () =>
  z
    .number({ invalid_type_error: 'validation.mustBeInteger' })
    .finite({ message: 'validation.mustBeInteger' })
    .int({ message: 'validation.mustBeInteger' })
    .max(MAX_MONEY_MINOR, { message: 'validation.amountTooLarge' });

/**
 * Сумма, которую вводит человек: строго больше нуля.
 *
 * Ноль здесь не «бесплатно», а «поле не заполнено»: операция на ноль рублей
 * и начисление на ноль не значат ничего, кроме забытого ввода.
 */
export const zMoney = money().min(1, { message: 'validation.amountRequired' });

/** Сумма, для которой ноль осмыслен: комиссия, оклад, аванс. */
export const zMoneyNonNegative = money().min(0, { message: 'validation.mustBePositive' });

/**
 * Сумма со знаком: начальный остаток кошелька и фактический остаток при
 * сверке. Оба могут быть отрицательными — кредитная карта и уход в минус.
 */
export const zMoneySigned = money().min(-MAX_MONEY_MINOR, {
  message: 'validation.amountTooLarge',
});

/** @deprecated Оставлено ради совместимости; используйте zMoneyNonNegative. */
export const zMinorAmount = zMoneyNonNegative;

/** @deprecated Оставлено ради совместимости; используйте zMoneySigned. */
export const zSignedMinorAmount = zMoneySigned;

export const zPercent = z
  .number()
  .min(0, { message: 'validation.mustBePositive' })
  .max(100, { message: 'validation.maxPercent' });

/** Ставка налога: допускаем дробные (например, 13.5%). */
export const zTaxRate = z.number().min(0).max(100);

export const zNonEmptyString = (max = 200) => z.string().trim().min(1, { message: 'validation.required' }).max(max);

export const zOptionalString = (max = 500) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullish();

export const zDayOfMonth = z
  .number()
  .int()
  .refine((value) => value === -1 || (value >= 1 && value <= 31), {
    message: 'validation.invalidDayOfMonth',
  });

export const zColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, { message: 'validation.invalidColor' });

export const zPagination = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type Pagination = z.infer<typeof zPagination>;

export const zSort = z.object({
  sortBy: z.string().max(40).optional(),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
});

export const zDateRange = z.object({
  from: zISODate.optional(),
  to: zISODate.optional(),
});

/** Ответ-обёртка для списков. */
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/** Кастомные поля профиля хранятся как свободный JSON-объект. */
export const zCustomFields = z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).default({});

export const zEnumOf = <T extends readonly [string, ...string[]]>(values: T) => z.enum(values);
